// Radha — enterprise readiness, surfaced in Settings: business entities
// (multi-GSTIN), API keys, outbound webhooks, custom field definitions, and
// SSO request tracking. SSO itself is NOT wired up — see the note in the
// component; everything else here is real and verified.
import React, { useState } from "react";

import {
  useBusinessEntities, useAddBusinessEntity,
  useApiKeys, useCreateApiKey, useRevokeApiKey,
  useWebhookSubscriptions, useAddWebhook, useRemoveWebhook,
  useSsoRequests, useRequestSso,
  useCustomFieldDefinitions, useAddCustomFieldDefinition, useRemoveCustomFieldDefinition,
  enterpriseErrorMessage,
} from "../hooks/useEnterprise";
import { fmtDate } from "../lib/money";

const EVENT_TYPES = ["invoice.created", "payment.captured"];

function Section({ title, help, children }: { title: string; help: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 6 }}>{title}</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 12px" }}>{help}</p>
      {children}
    </div>
  );
}

function BusinessEntities({ canWrite }: { canWrite: boolean }) {
  const { data: entities } = useBusinessEntities();
  const add = useAddBusinessEntity();
  const [name, setName] = useState("");
  const [gstin, setGstin] = useState("");
  const [note, setNote] = useState<string | null>(null);

  return (
    <Section title="Business entities" help="For a group with more than one legal entity or GSTIN — invoices and accounts can be tagged to an entity for consolidated or per-entity reporting.">
      {(entities ?? []).map((e) => (
        <div key={e.id} style={{ fontSize: 13, padding: "4px 0" }}>{e.legal_name}{e.gstin ? ` · ${e.gstin}` : ""}{e.is_default ? " (default)" : ""}</div>
      ))}
      {(entities ?? []).length === 0 && <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>No additional entities — everything runs under the tenant's own GSTIN.</p>}
      {canWrite && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Legal name" style={{ flex: 1, minWidth: 140, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          <input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="GSTIN (optional)" style={{ width: 160, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          <button onClick={() => { setNote(null); add.mutate({ legal_name: name, gstin: gstin || undefined }, { onSuccess: () => { setName(""); setGstin(""); }, onError: (e) => setNote(enterpriseErrorMessage(e)) }); }}
            disabled={!name || add.isPending}
            style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            Add
          </button>
        </div>
      )}
      {note && <p style={{ fontSize: 12, color: "var(--status-critical)", marginTop: 4 }}>{note}</p>}
    </Section>
  );
}

function ApiKeys({ canWrite }: { canWrite: boolean }) {
  const { data: keys } = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();
  const [label, setLabel] = useState("");
  const [revealed, setRevealed] = useState<{ key: string; prefix: string } | null>(null);
  const [note, setNote] = useState<string | null>(null);

  return (
    <Section title="Public API" help="Read-only access (invoices, collection accounts) for external systems — a key authenticates via Authorization: Bearer <key>, no Supabase login involved.">
      {(keys ?? []).map((k) => (
        <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "4px 0" }}>
          <span style={{ flex: 1 }}>{k.label}</span>
          <code style={{ fontSize: 11.5, color: "var(--fg-muted)" }}>{k.key_prefix}…</code>
          <span style={{ fontSize: 11, color: k.revoked_at ? "var(--status-critical)" : "var(--status-good)" }}>{k.revoked_at ? "revoked" : "active"}</span>
          {canWrite && !k.revoked_at && (
            <button onClick={() => revoke.mutate(k.id)} style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-faint)", cursor: "pointer" }}>revoke</button>
          )}
        </div>
      ))}
      {canWrite && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Key label (e.g. 'BI tool')" style={{ flex: 1, minWidth: 140, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          <button onClick={() => { setNote(null); setRevealed(null); create.mutate(label, { onSuccess: (r) => { setRevealed(r); setLabel(""); }, onError: (e) => setNote(enterpriseErrorMessage(e)) }); }}
            disabled={!label || create.isPending}
            style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            Create key
          </button>
        </div>
      )}
      {note && <p style={{ fontSize: 12, color: "var(--status-critical)", marginTop: 4 }}>{note}</p>}
      {revealed && (
        <div style={{ marginTop: 8, padding: 10, background: "var(--surface-2)", border: "1px solid var(--status-warning)", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "var(--status-warning)", marginBottom: 4 }}>Copy this now — it won't be shown again</div>
          <code style={{ fontSize: 12, wordBreak: "break-all" }}>{revealed.key}</code>
        </div>
      )}
    </Section>
  );
}

function Webhooks({ canWrite }: { canWrite: boolean }) {
  const { data: hooks } = useWebhookSubscriptions();
  const add = useAddWebhook();
  const remove = useRemoveWebhook();
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState<string[]>(["payment.captured"]);
  const [note, setNote] = useState<string | null>(null);

  const toggleEvent = (ev: string) => setEvents((cur) => cur.includes(ev) ? cur.filter((e) => e !== ev) : [...cur, ev]);

  return (
    <Section title="Webhooks" help="Real outbound delivery — invoice.created and payment.captured, HMAC-signed with your own secret (X-Radha-Signature header, SHA-256).">
      {(hooks ?? []).map((w) => (
        <div key={w.id} style={{ fontSize: 13, padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1, wordBreak: "break-all" }}>{w.url}</span>
          <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>{w.event_types.join(", ")}</span>
          {w.last_fired_at && <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>last fired {fmtDate(w.last_fired_at)}</span>}
          {canWrite && <button onClick={() => remove.mutate(w.id)} style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-faint)", cursor: "pointer" }}>remove</button>}
        </div>
      ))}
      {canWrite && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-system.com/webhooks/radha" style={{ flex: 1, minWidth: 180, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
            <input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Signing secret" style={{ width: 160, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
            {EVENT_TYPES.map((ev) => (
              <label key={ev} style={{ fontSize: 12.5, display: "flex", alignItems: "center", gap: 4 }}>
                <input type="checkbox" checked={events.includes(ev)} onChange={() => toggleEvent(ev)} /> {ev}
              </label>
            ))}
          </div>
          <button onClick={() => { setNote(null); add.mutate({ url, secret, event_types: events }, { onSuccess: () => { setUrl(""); setSecret(""); }, onError: (e) => setNote(enterpriseErrorMessage(e)) }); }}
            disabled={!url || !secret || events.length === 0 || add.isPending}
            style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            Add webhook
          </button>
        </div>
      )}
      {note && <p style={{ fontSize: 12, color: "var(--status-critical)", marginTop: 4 }}>{note}</p>}
    </Section>
  );
}

function CustomFields({ canWrite }: { canWrite: boolean }) {
  const { data: fields } = useCustomFieldDefinitions();
  const add = useAddCustomFieldDefinition();
  const remove = useRemoveCustomFieldDefinition();
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");

  return (
    <Section title="Custom fields" help="Tenant-defined fields on collection accounts (v1) — set per-account from the Collections tab once defined here.">
      {(fields ?? []).map((f) => (
        <div key={f.id} style={{ fontSize: 13, padding: "4px 0", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1 }}>{f.field_label} <span style={{ color: "var(--fg-faint)", fontSize: 11 }}>({f.field_type})</span></span>
          {canWrite && <button onClick={() => remove.mutate(f.id)} style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-faint)", cursor: "pointer" }}>remove</button>}
        </div>
      ))}
      {canWrite && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input value={label} onChange={(e) => { setLabel(e.target.value); setKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")); }} placeholder="Field label" style={{ flex: 1, minWidth: 140, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          <select value={type} onChange={(e) => setType(e.target.value)} style={{ padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }}>
            <option value="text">Text</option><option value="number">Number</option><option value="date">Date</option><option value="boolean">Yes/No</option>
          </select>
          <button onClick={() => { add.mutate({ field_key: key, field_label: label, field_type: type }, { onSuccess: () => { setLabel(""); setKey(""); } }); }}
            disabled={!label || add.isPending}
            style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            Add field
          </button>
        </div>
      )}
    </Section>
  );
}

function SsoRequests({ canWrite }: { canWrite: boolean }) {
  const { data: requests } = useSsoRequests();
  const request = useRequestSso();
  const [idp, setIdp] = useState("");
  const [contact, setContact] = useState("");

  return (
    <Section title="Single sign-on (SSO)" help="Not built — SAML SSO needs a paid Supabase add-on plus your identity provider's live metadata to configure and test against, neither of which exists in this environment. This just logs the request so it's tracked, not automated.">
      {(requests ?? []).map((r) => (
        <div key={r.id} style={{ fontSize: 13, padding: "4px 0" }}>{r.idp_name} ({r.idp_type.toUpperCase()}) — <span style={{ color: "var(--fg-muted)" }}>{r.status}</span></div>
      ))}
      {canWrite && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input value={idp} onChange={(e) => setIdp(e.target.value)} placeholder="Identity provider (e.g. Azure AD)" style={{ flex: 1, minWidth: 160, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact email" style={{ width: 200, padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          <button onClick={() => request.mutate({ idp_name: idp, idp_type: "saml", contact_email: contact || undefined }, { onSuccess: () => { setIdp(""); setContact(""); } })}
            disabled={!idp || request.isPending}
            style={{ padding: "6px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            Request SSO
          </button>
        </div>
      )}
    </Section>
  );
}

export function EnterpriseSettings({ canWrite }: { canWrite: boolean }) {
  return (
    <div>
      <BusinessEntities canWrite={canWrite} />
      <ApiKeys canWrite={canWrite} />
      <Webhooks canWrite={canWrite} />
      <CustomFields canWrite={canWrite} />
      <SsoRequests canWrite={canWrite} />
    </div>
  );
}
